import { AttributeValueInput } from "../types";
import { MappingContext } from "../orchestrator";
import { measurementMapperConfigSchema } from "../schemas";
import { memoizeMapperFactory } from "../utils/memoize";

const POUNDS_TO_KG = 0.45359237;

/**
 * Internal implementation of weightMapper without memoization.
 */
function weightMapperImpl(config: unknown) {
  const parsedConfig = measurementMapperConfigSchema.parse(config);

  return (response: string, _context: MappingContext): AttributeValueInput[] => {
    const weightInPounds = parseFloat(response);
    if (isNaN(weightInPounds)) {
      return [];
    }
    const weightInKg = Math.round(weightInPounds * POUNDS_TO_KG * 10) / 10;

    return [
      {
        attributeDefinitionId: parsedConfig.attributeDefinitionId,
        floatAttributeValue: weightInKg,
        isSkipped: false,
      },
    ];
  };
}

/**
 * Creates a mapper for weight, converting from pounds to kilograms.
 * This function is memoized to avoid repeated config parsing.
 */
export const weightMapper = memoizeMapperFactory(weightMapperImpl);

/**
 * Internal implementation of heightMapper without memoization.
 */
function heightMapperImpl(config: unknown) {
  const parsedConfig = measurementMapperConfigSchema.parse(config);

  return (response: string, _context: MappingContext): AttributeValueInput[] => {
    const height = parseFloat(response);
    if (isNaN(height)) {
      return [];
    }

    // The standardHeightQuestion values are in cm, so no conversion needed
    // if coming from there. This is a safeguard if another unit is used.
    // Assuming 'response' is a stringified number representing cm.
    return [
      {
        attributeDefinitionId: parsedConfig.attributeDefinitionId,
        floatAttributeValue: height,
        isSkipped: false,
      },
    ];
  };
}

/**
 * Creates a mapper for height, assuming the input is in inches or cm
 * and the target system expects cm.
 * This function is memoized to avoid repeated config parsing.
 */
export const heightMapper = memoizeMapperFactory(heightMapperImpl); 